import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Package, ChevronRight, MapPin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiGet, fmt, API_BASE } from '../lib/api';

async function apiDelete(path) {
  const res = await fetch(`${API_BASE}${path}`, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw new Error('Delete failed');
  return res.json();
}

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) nav('/login?next=/account');
  }, [user, loading, nav]);

  useEffect(() => {
    if (user) {
      apiGet('/orders/mine').then(setOrders).catch(() => setOrders([]));
      apiGet('/users/me/addresses').then(setAddresses).catch(() => {});
    }
  }, [user]);

  const removeAddress = async (id) => {
    try {
      await apiDelete(`/users/me/addresses/${id}`);
      setAddresses((a) => a.filter((x) => x.id !== id));
      toast.success('Address removed');
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading || !user) {
    return <div className="bg-cream min-h-screen pt-40"><div className="max-w-3xl mx-auto px-4 animate-pulse space-y-4"><div className="h-10 bg-silver rounded w-1/3" /><div className="h-40 bg-silver rounded-2xl" /></div></div>;
  }

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-forest noise relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <span data-testid="account-avatar" className="grid place-items-center w-16 h-16 rounded-full bg-flame font-display font-bold text-3xl text-white uppercase overflow-hidden">
              {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user.name.charAt(0)}
            </span>
            <div>
              <h1 data-testid="account-name" className="font-display font-extrabold uppercase tracking-tight text-cream text-3xl sm:text-4xl">{user.name}</h1>
              <p className="text-cream/60 text-sm mt-1">{user.email} · {user.role === 'admin' ? 'Administrator' : 'Customer'}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); nav('/'); }}
            className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-flame text-cream hover:text-flame font-display font-semibold uppercase tracking-wide px-6 py-3 rounded-full transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        <div>
          <h2 className="font-display font-bold uppercase text-forest text-2xl mb-6 flex items-center gap-2">
            <Package className="w-5 h-5 text-flame" /> Order History
          </h2>
          {orders === null ? (
            <div className="space-y-3 animate-pulse">
              {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-white border border-emerald-950/10 rounded-2xl" />)}
            </div>
          ) : orders.length === 0 ? (
            <div data-testid="account-no-orders" className="bg-white border border-emerald-950/10 rounded-2xl p-10 text-center">
              <p className="font-display font-bold uppercase text-forest text-2xl">No orders yet</p>
              <p className="text-sm text-sub mt-2 mb-6">Your placed orders will show up here.</p>
              <Link to="/shop" data-testid="account-shop-link" className="inline-block bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-7 py-3.5 rounded-full transition-colors">Shop Parts</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/order/${o.order_number}`}
                  data-testid={`account-order-${o.order_number}`}
                  className="group flex flex-wrap items-center gap-4 bg-white border border-emerald-950/10 hover:border-flame/50 rounded-2xl px-5 py-4 transition-colors"
                >
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-mono text-sm font-semibold text-forest">{o.order_number}</p>
                    <p className="text-xs text-sub mt-0.5">
                      {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {o.items.length} item{o.items.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-ok/10 text-ok">{o.status.replace(/_/g, ' ')}</span>
                  <span className="font-display font-bold text-xl text-forest">{fmt(o.total)}</span>
                  <ChevronRight className="w-4 h-4 text-sub group-hover:text-flame transition" />
                </Link>
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="font-display font-bold uppercase text-forest text-2xl mb-2 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-flame" /> Saved Addresses
          </h2>
          <p className="text-sm text-sub mb-6">Saved at checkout with the "save this address" option — tap one next time for one-tap checkout.</p>
          {addresses.length === 0 ? (
            <div data-testid="account-no-addresses" className="bg-white border border-emerald-950/10 rounded-2xl p-8 text-center">
              <p className="font-semibold text-forest">No saved addresses yet</p>
              <p className="text-sm text-sub mt-1">Tick "Save this address" during checkout to add one.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {addresses.map((a) => (
                <div key={a.id} data-testid={`account-address-${a.id}`} className="bg-white border border-emerald-950/10 rounded-2xl p-5 flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-flame shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-forest">{a.label}</p>
                    <p className="text-sm text-sub mt-1 leading-relaxed">{a.name}<br />{a.address}<br />{a.city}, {a.state} {a.zip}</p>
                  </div>
                  <button data-testid={`account-address-delete-${a.id}`} onClick={() => removeAddress(a.id)} className="p-2 text-sub hover:text-bad transition" aria-label="Delete address">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
